import { AfterViewInit, Component, ElementRef, Inject, Renderer2, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@injectables/services/auth/auth.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { Environment, ENVIRONMENT } from '@shared/tokens/environment';
import { MfeHelperService } from '@injectables/services/mfe-helper/mfe-helper.service';
import { ErrorHandlerService } from '@injectables/services/errors/error-handler.service';
import { PurchaseStateService } from '@injectables/services/purchase/purchase-state.service';

@Component({
	selector: 'app-mfe-subscription-checkout',
	templateUrl: './mfe-subscription-checkout.component.html',
	standalone: true,
})
export class MfeSubscriptionCheckoutComponent implements AfterViewInit, OnDestroy {
	MFE_SCRIPT_URL = this.environment.frontendComponents.scripts.purchaseWeb;
	MFE_TAG = 'craftnote-subscription-checkout';
	@ViewChild('componentContainer') element: ElementRef;
	private authToken: string;
	private authTokenSubscription: Subscription;
	private webComponent: HTMLElement | undefined;
	private darkMode: boolean;

	constructor(
		@Inject(ENVIRONMENT) private readonly environment: Environment,
		private readonly renderer: Renderer2,
		private readonly authService: AuthService,
		private readonly router: Router,
		private readonly mfeHelperService: MfeHelperService,
		private readonly errorHandlerService: ErrorHandlerService,
		private readonly purchaseStateService: PurchaseStateService,
	) { }

	async ngAfterViewInit(): Promise<void> {
		const [authToken, _, isDarkModeEnabled] = await Promise.all([
			firstValueFrom(this.authService.authToken()),
			this.loadScript(),
			this.mfeHelperService.isDarkModeEnabled()
		]);

		this.authToken = authToken;
		this.darkMode = isDarkModeEnabled;
		this.injectComponent();
		this.authTokenSubscription = this.authService.authToken().subscribe(token => {
			this.authToken = token;
			if (this.webComponent) {
				this.renderer.setAttribute(this.webComponent, 'auth-token', token);
			}
		});

	}

	private loadScript(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.mfeHelperService.loadScript(
				this.MFE_SCRIPT_URL,
				this.MFE_TAG,
				() => resolve(),
				() => {
					this.errorHandlerService.handleError(
						`Error Loading in script ${this.MFE_SCRIPT_URL} from ${this.MFE_TAG}`
					);
					reject();
				}
			);
		});
	}

	ngOnDestroy(): void {
		if (this.authTokenSubscription) {
			this.authTokenSubscription.unsubscribe();
		}
	}

	private injectComponent = (): void => {
		this.webComponent = this.mfeHelperService.getMfe(this.MFE_TAG);
		if (!this.webComponent) {
			this.webComponent = this.renderer.createElement(this.MFE_TAG);
		}
		this.renderer.setAttribute(this.webComponent, 'auth-token', this.authToken);
		this.renderer.setAttribute(this.webComponent, 'language', this.mfeHelperService.getLanguage());
		this.renderer.setAttribute(this.webComponent, 'dark-mode', this.darkMode ? 'true' : 'false');
		this.renderer.listen(
			this.webComponent,
			'purchaseConfirmationCTAClicked',
			this.onConfirmationCTAClicked,
		);
		this.renderer.listen(
			this.webComponent,
			'alreadySubscribed',
			this.onAlreadySubscribed,
		);
		this.renderer.listen(this.webComponent, 'checkoutCompleted', this.onCheckoutCompleted);
		this.renderer.listen(this.webComponent, 'reloadTriggered', this.onReloadTriggered);
		this.renderer.listen(this.webComponent, 'backButtonClicked', this.onBackButtonClicked);

		const stripeContainer = this.renderer.createElement('div');
		this.renderer.setAttribute(stripeContainer, 'id', 'js-stripe-checkout');
		this.renderer.setAttribute(stripeContainer, 'slot', 'stripe-checkout');

		this.renderer.appendChild(this.webComponent, stripeContainer);
		this.renderer.appendChild(this.element.nativeElement, this.webComponent);
	};

	private onConfirmationCTAClicked = (event: any): void => {
		const detail = event?.detail;
		if (detail === 'LICENSE_ALLOCATION') {
			return void this.router.navigateByUrl('/settings/subscription');
		}
		return void this.router.navigateByUrl('/');
	};

	private onReloadTriggered = (_): void => {
		window.location.reload();
	};

	private onBackButtonClicked = (): void => {
		window.history.back();
	};

	private onAlreadySubscribed = (): void => {
		void this.router.navigateByUrl('/settings/subscription');
	};

	private onCheckoutCompleted = (): void => {
		void this.purchaseStateService.optimimisticForceTrialEnded();
	};
}
